import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessagesSquare, Loader2, Pencil, Check, X, UserPlus, Crown, LogOut, Camera, Search, Trash2, Users, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/common/Avatar";
import { VerifiedCheck } from "@/components/common/TrustBadge";
import { cn } from "@/lib/utils";
import { CIRCLE_MEMBER_CAP } from "@/components/chat/NewCircleDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function GroupInfoDrawer({ chatId, open, onOpenChange }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [editName, setEditName] = useState(false);
  const [nameVal, setNameVal] = useState("");
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");

  const { data: group, isLoading } = useQuery({
    queryKey: ["group", chatId],
    queryFn: () => api.circleGroup(chatId),
    enabled: open && !!chatId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["group", chatId] });
    qc.invalidateQueries({ queryKey: ["messages", chatId] });
    qc.invalidateQueries({ queryKey: ["chats"] });
    qc.invalidateQueries({ queryKey: ["circles-list"] });
  };

  const isOwner = group?.is_owner;
  const members = group?.members || [];
  const atCap = (group?.member_count || members.length) >= (group?.max_members || CIRCLE_MEMBER_CAP);

  const renameMut = useMutation({
    mutationFn: (payload) => api.renameCircle(chatId, payload),
    onSuccess: () => { invalidate(); setEditName(false); toast.success("Circle updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't update the Circle."),
  });

  const removeMut = useMutation({
    mutationFn: (uid) => api.removeCircleMember(chatId, uid),
    onSuccess: () => { invalidate(); toast.success("Removed from Circle"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't remove member."),
  });

  const promoteMut = useMutation({
    mutationFn: (uid) => api.promoteGroupAdmin(chatId, uid),
    onSuccess: () => { invalidate(); toast.success("Promoted to admin"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't promote member."),
  });

  const addMut = useMutation({
    mutationFn: (uid) => api.addCircleMember(chatId, uid),
    onSuccess: () => { invalidate(); toast.success("Added to Circle"); setQ(""); setAdding(false); },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      if (d?.code === "member_limit" || d?.error === "member_limit") {
        toast.error(d.message || `Circle is full — max ${d.max || CIRCLE_MEMBER_CAP} members.`);
      } else {
        toast.error((typeof d === "string" && d) || d?.message || "Couldn't add member.");
      }
    },
  });

  const leaveMut = useMutation({
    mutationFn: () => api.leaveCircle(chatId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chats"] });
      qc.invalidateQueries({ queryKey: ["circles-list"] });
      onOpenChange(false);
      toast.success("You left the Circle");
      navigate("/app/circles");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't leave the Circle."),
  });

  const { data: searchResults = [], isFetching } = useQuery({
    queryKey: ["user-search", q],
    queryFn: () => api.searchUsers(q),
    enabled: open && adding && q.trim().length >= 1,
  });
  const memberIds = new Set(members.map((m) => m.id));

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => renameMut.mutate({ icon_b64: reader.result });
    reader.readAsDataURL(f);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white" data-testid="group-info-drawer">
        <DialogHeader>
          <DialogTitle className="font-heading">Circle info</DialogTitle>
        </DialogHeader>

        {isLoading || !group ? (
          <div className="flex justify-center py-10 text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {/* Identity */}
            <div className="flex items-center gap-3">
              <div className="relative">
                {group.icon_b64 ? (
                  <img src={group.icon_b64} alt="" className="h-16 w-16 rounded-2xl object-cover" />
                ) : (
                  <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-bloq-mint/15 text-bloq-mint"><MessagesSquare className="h-7 w-7" /></span>
                )}
                {isOwner && (
                  <button onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-bloq-s1 bg-bloq-mint text-bloq-bg" data-testid="group-photo-btn" title="Change photo">
                    <Camera className="h-3.5 w-3.5" />
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="group-photo-input" />
              </div>
              <div className="min-w-0 flex-1">
                {editName ? (
                  <div className="flex items-center gap-1.5">
                    <input value={nameVal} onChange={(e) => setNameVal(e.target.value.slice(0, 40))} className="h-9 w-full rounded-lg border border-white/10 bg-bloq-bg px-2 text-sm outline-none focus:border-bloq-mint/40" data-testid="group-name-input" autoFocus />
                    <button onClick={() => renameMut.mutate({ name: nameVal.trim() })} disabled={!nameVal.trim() || renameMut.isPending} className="rounded-lg bg-bloq-mint p-2 text-bloq-bg disabled:opacity-40" data-testid="group-name-save"><Check className="h-4 w-4" /></button>
                    <button onClick={() => setEditName(false)} className="rounded-lg bg-white/8 p-2"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-lg font-bold" data-testid="group-name">{group.name || "Circle"}</h3>
                    {isOwner && (
                      <button onClick={() => { setNameVal(group.name || ""); setEditName(true); }} className="text-bloq-muted hover:text-white" data-testid="group-rename-btn"><Pencil className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                )}
                <div className="mt-0.5 flex items-center gap-1 text-xs text-bloq-muted">
                  <Users className="h-3.5 w-3.5" /> {group.member_count || members.length} of {group.max_members || CIRCLE_MEMBER_CAP} members
                </div>
              </div>
            </div>

            {/* Add member */}
            {isOwner && (
              <div>
                {!adding ? (
                  <button onClick={() => setAdding(true)} disabled={atCap} className="flex w-full items-center justify-center gap-2 rounded-xl border border-bloq-mint/25 bg-bloq-mint/[0.06] py-2.5 text-sm font-semibold text-bloq-mint transition-colors hover:bg-bloq-mint/10 disabled:opacity-40" data-testid="group-add-member-btn">
                    <UserPlus className="h-4 w-4" /> {atCap ? `Circle is full (${group.max_members || CIRCLE_MEMBER_CAP})` : "Add people"}
                  </button>
                ) : (
                  <div className="rounded-xl border border-white/10 bg-bloq-bg p-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-bloq-muted" />
                      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people to add" className="h-9 w-full rounded-lg bg-transparent pl-8 pr-2 text-sm outline-none" autoFocus data-testid="group-add-search" />
                    </div>
                    <div className="mt-1 max-h-40 space-y-0.5 overflow-y-auto">
                      {isFetching && <div className="flex justify-center py-3 text-bloq-muted"><Loader2 className="h-4 w-4 animate-spin" /></div>}
                      {searchResults.filter((u) => !memberIds.has(u.id)).map((u) => (
                        <button key={u.id} onClick={() => addMut.mutate(u.id)} disabled={addMut.isPending} className="flex w-full items-center gap-2 rounded-lg p-1.5 text-left hover:bg-white/5 disabled:opacity-50" data-testid={`group-add-${u.username}`}>
                          <Avatar user={u} size="xs" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold">{u.display_name}</div>
                            <div className="truncate text-xs text-bloq-muted">@{u.username}</div>
                          </div>
                          <UserPlus className="h-4 w-4 text-bloq-mint" />
                        </button>
                      ))}
                    </div>
                    <button onClick={() => { setAdding(false); setQ(""); }} className="mt-1 w-full rounded-lg py-1.5 text-xs text-bloq-muted hover:text-white">Done</button>
                  </div>
                )}
              </div>
            )}

            {isOwner && (
              <div data-testid="group-who-can-add">
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted">Who can add members</div>
                <div className="flex gap-1 rounded-xl border border-white/10 bg-bloq-bg p-1">
                  {[{ v: "owner", l: "Only me" }, { v: "members", l: "Everyone" }].map((o) => {
                    const activeOpt = (group.who_can_add || "owner") === o.v;
                    return (
                      <button
                        key={o.v}
                        onClick={() => !activeOpt && renameMut.mutate({ who_can_add: o.v })}
                        disabled={renameMut.isPending}
                        className={cn(
                          "flex-1 rounded-lg py-1.5 text-xs font-semibold transition-colors",
                          activeOpt ? "bg-bloq-mint/15 text-bloq-mint" : "text-bloq-muted hover:text-white",
                        )}
                        data-testid={`who-can-add-${o.v}`}
                      >
                        {o.l}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Members */}
            <div>
              <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted">Members</div>
              <div className="space-y-1" data-testid="group-members-list">
                {members.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 rounded-xl px-1 py-1.5" data-testid={`group-member-${m.username}`}>
                    <Avatar user={m} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-semibold">{m.display_name}</span>
                        {m.verified && <VerifiedCheck size={12} />}
                        {m.id === group.owner_id && <span className="flex items-center gap-0.5 rounded-full bg-bloq-purple/15 px-1.5 py-0.5 text-[10px] font-bold text-bloq-purple"><Crown className="h-2.5 w-2.5" /> Owner</span>}
                        {m.id !== group.owner_id && (m.is_admin || m.role === "admin") && <span className="flex items-center gap-0.5 rounded-full bg-bloq-blue/15 px-1.5 py-0.5 text-[10px] font-bold text-bloq-blue" data-testid={`group-admin-badge-${m.username}`}><Shield className="h-2.5 w-2.5" /> Admin</span>}
                      </div>
                      <div className="truncate text-xs text-bloq-muted">@{m.username}</div>
                    </div>
                    {isOwner && m.id !== group.owner_id && (
                      <div className="flex shrink-0 items-center gap-1">
                        {!(m.is_admin || m.role === "admin") && (
                          <button onClick={() => promoteMut.mutate(m.id)} disabled={promoteMut.isPending} className="rounded-full p-1.5 text-bloq-muted hover:bg-bloq-blue/15 hover:text-bloq-blue disabled:opacity-40" data-testid={`group-promote-${m.username}`} title="Make admin">
                            <Shield className="h-4 w-4" />
                          </button>
                        )}
                        <button onClick={() => removeMut.mutate(m.id)} className="rounded-full p-1.5 text-bloq-muted hover:bg-destructive/15 hover:text-destructive" data-testid={`group-remove-${m.username}`} title="Remove">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Leave */}
            <button onClick={() => leaveMut.mutate()} disabled={leaveMut.isPending} className="flex w-full items-center justify-center gap-2 rounded-xl border border-destructive/30 py-2.5 text-sm font-semibold text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50" data-testid="group-leave-btn">
              {leaveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />} Leave Circle
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
